import React, { Component } from 'react';
import { connect } from 'react-redux';
import ServiceEditor from './ServiceEditor';
import ServiceConfiguration from './ServiceConfiguration';
import ServiceList from './ServiceList';
import ServicePreview from './ServicePreview';
import NewServiceSelect from './NewServiceSelect';
import NewFlowService from './NewFlowService';
import NewCustomService from './NewCustomService';
import NewIntegrationService from './NewIntegrationService';
import { bindMethods, stringReplace, isNullOrEmpty } from '../../utils/core';
import {
    getServiceConfigurationValues,
    getServiceTypesAndActions,
    installService,
    getService,
    saveService,
    deleteService,
} from '../../sources/service';
import { populateServiceValueRequest } from '../../utils/ajax';
import Model from '../../utils/model';
import '../../../css/notification.less';
import ServiceDelete from './ServiceDelete';
import { addNotification } from '../../actions/reduxActions/notification';
import translations from '../../translations';
import { SERVICE_VIEWS } from './ServiceConstants';

const initialState = {
    // Basic user-entered information about a new service
    newServiceData: null,
    // Configuration values for the service from getServiceConfigurationValues
    serviceConfiguration: null,
    // Types and Actions for the service from getServiceTypesAndActions
    typesAndActions: null,
    // Information about an installed Service from /service api
    currentServiceData: null,
    // A service value request populated with configuration values for a service, used for getServiceTypesAndActions & installService
    populatedServiceValueRequests: null,
    // The type of atom they selected when installing their service
    atomType: null,
    // The view for service they selected when they installed a new service
    selectedServiceView: null,
};
export class ServiceController extends Component {

    state = {
        view: SERVICE_VIEWS.ServiceList,
        ...initialState,
    }

    constructor(props) {
        super(props);

        bindMethods(
            this,
            'switchView',
            'installServiceAndChangeView',
            'switchToNewService',
            'selectServiceType',
            'setNewServiceDetails',
            'onDeleteService',
            'deleteServiceAndChangeView',
        );
    }

    /**
     * @param {Object} service
     *
     * @description Queries a specific service for its configuration details
     * which can then be passed on to the "editing view".
     */
    onEditService = async (service) => {
        this.setState({ view: SERVICE_VIEWS.LoadingServiceEditor });
        // The information obtained from the list doesn't have Types, so we need to query the service specifically
        return getService({
            token: this.props.authenticationToken,
            serviceId: service.id,
        }).then(currentServiceData => this.setState({
            currentServiceData,
            view: SERVICE_VIEWS.ServiceEditor,
        })).catch((error) => {
            this.setState({
                view: SERVICE_VIEWS.ServiceConfiguration,
            }, () => {
                this.props.addNotification({
                    type: 'error',
                    message: error.message,
                    isPersistent: true,
                });
            });
        });
    }

    /**
     * @param {String} name The name of the service set by the user
     * @param {String} url The specific services endpoint
     * @param {String} [username] This is applicable to services that leverage basic auth
     * @param {String} [password] This is applicable to services that leverage basic auth
     * @param {String} [comments] Comments created by the user
     *
     * @description Saves a service and all of its configuration settings
     */
    onSaveService = async (name, url, username, password, comments) => {

        this.setState({ view: SERVICE_VIEWS.LoadingSavingService });

        const serviceData = Object.assign(
            {},
            this.state.currentServiceData,
            {
                developerName: name,
                uri: url,
                username,
                password,
                developerSummary: comments,
            },
        );

        return saveService({ token: this.props.authenticationToken, serviceData })
            .then(currentServiceData => this.setState({
                view: SERVICE_VIEWS.ServiceEditor,
                currentServiceData,
            })).catch((error) => {
                this.setState({
                    view: SERVICE_VIEWS.ServiceConfiguration,
                }, () => {
                    this.props.addNotification({
                        type: 'error',
                        message: error.message,
                        isPersistent: true,
                    });
                });
            });
    }

    onDeleteService(service) {
        this.setState({
            currentServiceData: service,
            view: SERVICE_VIEWS.ServiceDelete,
        });
    }

    setNewServiceDetails(detail, detailType) {
        const { newServiceData } = this.state;
        newServiceData[detailType] = detail;
        this.setState({ newServiceData });
    }

    /**
     * @param {String} name The name of the service set by the user
     * @param {String} url The specific services endpoint
     * @param {String} [username] This is applicable to services that leverage basic auth
     * @param {String} [password] This is applicable to services that leverage basic auth
     * @param {String} [comments] Comments created by the user
     * @param {String} [id] The ID of an existing service (GUID)
     *
     * @description Retrieves service configuration values that have either been set
     * (if the service has been previously installed/configured) or configuration value
     * placeholders if the service has been initially created.
     */
    switchToRefreshServiceConfig = async (name, url, username, password, comments, id = null) => {
        const {
            currentServiceData,
            newServiceData,
        } = this.state;

        const {
            authenticationToken,
            tenantId,
        } = this.props;

        const doesServiceAlreadyExist = isNullOrEmpty(id) === false;
        if (doesServiceAlreadyExist) {
            const newCurrentServiceData = Object.assign(
                {},
                currentServiceData,
                {
                    developerName: name,
                    uri: url,
                    username,
                    password,
                    developerSummary: comments,
                },
            );
            this.setState({
                currentServiceData: newCurrentServiceData,
                view: SERVICE_VIEWS.LoadingServiceConfiguration,
            });

        } else {
            const updatedServiceData = Object.assign(
                {},
                newServiceData,
                {
                    name, url, username, password, comments, id,
                },
            );

            this.setState({
                newServiceData: updatedServiceData,
                view: SERVICE_VIEWS.LoadingServiceConfiguration,
            });
        }

        return getServiceConfigurationValues({
            token: authenticationToken,
            tenantId,
            uri: url,
            basicUsername: username,
            basicPassword: password,
        }).then(response => this.setState({
            view: SERVICE_VIEWS.ServiceConfiguration,
            serviceConfiguration: response.outputs,
        })).catch((error) => {

            /**
             * If a service ID is passed in then we can assume
             * that we should be redirecting to the editing screen
             * for an existing service. Otherwise just redirect to the
             * respective screen for creating a new service.
             */
            const view = id ?
                SERVICE_VIEWS.ServiceEditor :
                this.state.selectedServiceView || SERVICE_VIEWS.NewServiceSelect;

            this.setState({ view }, () => {
                this.props.addNotification({
                    type: 'error',
                    message: error.message,
                    isPersistent: true,
                });
            });
        });
    }

    /**
     * @param {Array} configValueIds A list of objects that represent the configuration values
     * that the service accepts. The shape of each object is as follows:
        {
            contentType: 'ContentString',
            name: 'Name',
            typeElementId: null,
            value: null,
        }
      * @description Retrieves a service types and actions to be displayed
      * and switches to the appropriate view to render.
     */
    switchToPreview = async (configValueIds) => {
        const {
            currentServiceData,
            newServiceData,
            serviceConfiguration,
        } = this.state;

        const {
            authenticationToken,
            tenantId,
        } = this.props;

        this.setState({
            view: SERVICE_VIEWS.LoadingPreview,
        });

        const doesServiceAlreadyExist = isNullOrEmpty(currentServiceData) === false;

        const populatedServiceValueRequests = populateServiceValueRequest(
            serviceConfiguration,
            configValueIds,
        );

        this.setState({
            populatedServiceValueRequests,
        });

        return getServiceTypesAndActions({
            token: authenticationToken,
            tenantId,
            uri: doesServiceAlreadyExist ? currentServiceData.uri : newServiceData.url,
            populatedServiceValueRequests,
        }).then(typesAndActions => this.setState({
            typesAndActions,
            view: SERVICE_VIEWS.ServicePreview,
        })).catch((error) => {
            this.setState({
                view: SERVICE_VIEWS.ServiceConfiguration,
            }, () => {
                this.props.addNotification({
                    type: 'error',
                    message: error.message,
                    isPersistent: true,
                });
            });
        });
    }

    selectServiceType({ name, description, url, view }) {
        this.setState({
            newServiceData: {
                selectedServiceView: name,
                description,
                url,
            },
            view,
            selectedServiceView: view,
        });
    }

    installServiceAndChangeView(configValueIds) {
        const previousView = this.state.view;

        // If we have just been given new configuration values
        const populatedServiceValueRequests = configValueIds ?
            // Then create the request Object and use that
            populateServiceValueRequest(
                this.state.serviceConfiguration,
                configValueIds,
            ) :
            // Otherwise use what's in state (could be empty)
            this.state.populatedServiceValueRequests;

        // If there is currentServiceData (They are editing an existing service)
        const savedService = isNullOrEmpty(this.state.currentServiceData) === false ?
            // Then use that
            this.state.currentServiceData :
            // Otherwise create an Object to hold the new Service information
            ({
                developerName: this.state.newServiceData.name,
                developerSummary: this.state.newServiceData.comments,
                uri: this.state.newServiceData.url,
                username: this.state.newServiceData.username,
                password: this.state.newServiceData.password,
                id: Model.guid(),
            });

        this.setState({
            populatedServiceValueRequests,
            view: SERVICE_VIEWS.LoadingInstallingService,
        });

        const errorHandler = (error) => {
            this.switchView(previousView);
            this.props.addNotification({
                type: 'error',
                message: error.message,
                isPersistent: true,
            });
        };

        installService({
            token: this.props.authenticationToken,
            tenantId: this.props.tenantId,
            uri: savedService.uri,
            developerName: savedService.developerName,
            developerSummary: savedService.developerSummary,
            username: savedService.username,
            password: savedService.password,
            id: savedService.id,
            populatedServiceValueRequests,
        }).then(
            () => getService({
                token: this.props.authenticationToken,
                serviceId: savedService.id,
            }).then(currentServiceData => this.setState({
                currentServiceData,
                view: SERVICE_VIEWS.ServiceEditor,
            })).catch(errorHandler),
        ).catch(errorHandler);
    }

    switchToNewService() {
        this.setState({
            view: SERVICE_VIEWS.NewServiceSelect,
            ...initialState,
        });
    }

    switchView(newView) {

        this.setState({
            view: newView,
        });
    }

    deleteServiceAndChangeView({ id, developerName }) {
        this.switchView(SERVICE_VIEWS.LoadingDeletingService);

        deleteService({ token: this.props.authenticationToken, serviceId: id })
            .then(() => {
                this.switchView(SERVICE_VIEWS.ServiceList);
                this.props.addNotification({
                    type: 'success',
                    message: stringReplace(
                        translations.SC_successful_delete,
                        developerName,
                    ),
                    isPersistent: false,
                });
            }).catch((error) => {
                this.props.addNotification({
                    type: 'error',
                    message: error.message,
                    isPersistent: true,
                });
                this.switchView(SERVICE_VIEWS.ServiceDelete);
            });
    }

    render() {
        const { authenticationToken, tenantId } = this.props;
        const { view, currentServiceData, serviceConfiguration, newServiceData, typesAndActions, selectedServiceView } = this.state;

        switch (view) {
        case SERVICE_VIEWS.ServiceEditor:
            return (
                <ServiceEditor
                    authenticationToken={authenticationToken}
                    tenantId={tenantId}
                    switchView={this.switchView}
                    newServiceData={newServiceData}
                    currentServiceData={currentServiceData}
                    switchToRefreshServiceConfig={this.switchToRefreshServiceConfig}
                    saveService={this.onSaveService}
                />
            );
        case SERVICE_VIEWS.LoadingSavingService:
            return (
                <div className="full-height">
                    {React.createElement(manywho.component.getByName('wait'), { isVisible: true, message: 'Saving service changes' }, null)}
                </div>
            );

        case SERVICE_VIEWS.LoadingServiceEditor:
            return (
                <div className="full-height">
                    {React.createElement(manywho.component.getByName('wait'), { isVisible: true, message: 'Loading service editor' }, null)}
                </div>
            );

        case SERVICE_VIEWS.LoadingServiceConfiguration:
            return (
                <div className="full-height">
                    {React.createElement(manywho.component.getByName('wait'), { isVisible: true, message: 'Loading service config' }, null)}
                </div>
            );

        case SERVICE_VIEWS.LoadingPreview:
            return (
                <div className="full-height">
                    {React.createElement(manywho.component.getByName('wait'), { isVisible: true, message: 'Loading service preview' }, null)}
                </div>
            );

        case SERVICE_VIEWS.LoadingInstallingService:
            return (
                <div className="full-height">
                    {React.createElement(manywho.component.getByName('wait'), { isVisible: true, message: 'Installing the service' }, null)}
                </div>
            );

        case SERVICE_VIEWS.LoadingDeletingService:
            return (
                <div className="full-height">
                    {React.createElement(manywho.component.getByName('wait'), { isVisible: true, message: 'Deleting the service' }, null)}
                </div>
            );

        case SERVICE_VIEWS.ServiceConfiguration:
            return (
                <ServiceConfiguration
                    authenticationToken={authenticationToken}
                    tenantId={tenantId}
                    configuration={serviceConfiguration}
                    newServiceData={newServiceData}
                    currentServiceData={currentServiceData}
                    switchView={this.switchView}
                    switchToPreview={this.switchToPreview}
                    installService={this.installServiceAndChangeView}
                    selectedServiceView={selectedServiceView}
                />
            );

        case SERVICE_VIEWS.ServicePreview:
            return (
                <ServicePreview
                    typesAndActions={typesAndActions}
                    switchView={this.switchView}
                    installService={this.installServiceAndChangeView}
                    newServiceData={newServiceData}
                    currentServiceData={currentServiceData}
                />
            );

        case SERVICE_VIEWS.NewServiceSelect:
            return (
                <div className="full-height flex-column">
                    <div className="admin-page main-scroller">
                        <NewServiceSelect
                            selectServiceType={this.selectServiceType}
                        />
                    </div>
                    <div className="mw-footer container-fluid">
                        <button
                            type="button"
                            className="btn btn-default outcome"
                            onClick={() => this.switchView(SERVICE_VIEWS.ServiceList)}
                        >
                            {'Back'}
                        </button>
                    </div>
                </div>
            );

        case SERVICE_VIEWS.NewFlowService:
            return (
                <form
                    name="CreateServiceForm"
                    className="full-height flex-column"
                    onSubmit={() => this.switchToRefreshServiceConfig(
                        newServiceData.name,
                        newServiceData.url,
                        newServiceData.basicUsername,
                        newServiceData.basicPassword,
                        newServiceData.comments,
                    )}
                >
                    <div className="admin-page main-scroller">
                        <NewServiceSelect
                            selectedService={newServiceData.selectedServiceView}
                            selectServiceType={this.selectServiceType}
                        />
                        <NewFlowService
                            newServiceData={newServiceData}
                            setNewServiceDetails={this.setNewServiceDetails}
                        />
                    </div>
                    <div className="mw-footer container-fluid">
                        <button
                            type="button"
                            className="btn btn-default outcome"
                            onClick={() => this.switchView(SERVICE_VIEWS.ServiceList)}
                        >
                            {'Back'}
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary outcome"
                        >
                            {'Retrieve Service Configuration Data'}
                        </button>
                    </div>
                </form>
            );

        case SERVICE_VIEWS.NewCustomService:
            return (
                <form
                    name="CreateServiceForm"
                    className="full-height flex-column"
                    onSubmit={() => this.switchToRefreshServiceConfig(
                        newServiceData.name,
                        newServiceData.url,
                        newServiceData.basicUsername,
                        newServiceData.basicPassword,
                        newServiceData.comments,
                    )}
                >
                    <div className="admin-page main-scroller">
                        <NewServiceSelect
                            selectedService={newServiceData.selectedServiceView}
                            selectServiceType={this.selectServiceType}
                        />
                        <NewCustomService
                            newServiceData={newServiceData}
                            setNewServiceDetails={this.setNewServiceDetails}
                        />
                    </div>
                    <div className="mw-footer container-fluid">
                        <button
                            type="button"
                            className="btn btn-default outcome"
                            onClick={() => this.switchView(SERVICE_VIEWS.ServiceList)}
                        >
                            {'Back'}
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary outcome"
                        >
                            {'Retrieve Service Configuration Data'}
                        </button>
                    </div>
                </form>
            );

        case SERVICE_VIEWS.NewIntegrationService:
            return (
                <form
                    name="CreateServiceForm"
                    className="full-height flex-column"
                    onSubmit={() => this.switchToRefreshServiceConfig(
                        newServiceData.name,
                        newServiceData.baseUrl ? newServiceData.baseUrl + newServiceData.pathToService : newServiceData.url,
                        newServiceData.basicUsername,
                        newServiceData.basicPassword,
                        newServiceData.comments,
                    )}
                >
                    <div className="admin-page main-scroller">
                        <NewServiceSelect
                            selectedService={newServiceData.selectedServiceView}
                            selectServiceType={this.selectServiceType}
                        />
                        <NewIntegrationService
                            newServiceData={newServiceData}
                            setNewServiceDetails={this.setNewServiceDetails}
                        />
                    </div>
                    <div className="mw-footer container-fluid">
                        <button
                            type="button"
                            className="btn btn-default outcome"
                            onClick={() => this.switchView(SERVICE_VIEWS.ServiceList)}
                        >
                            {'Back'}
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary outcome"
                        >
                            {'Retrieve Service Configuration Data'}
                        </button>
                    </div>
                </form>
            );

        case SERVICE_VIEWS.ServiceDelete:
            return (
                <ServiceDelete
                    serviceData={currentServiceData}
                    switchView={this.switchView}
                    deleteService={this.deleteServiceAndChangeView}
                />
            );

        case SERVICE_VIEWS.ServiceList:
        default:
            return (
                <ServiceList
                    authenticationToken={authenticationToken}
                    tenantId={tenantId}
                    onAddService={() => {}}
                    onEditService={this.onEditService}
                    onDeleteService={this.onDeleteService}
                    switchToNewService={this.switchToNewService}
                />
            );
        }
    }
}

const mapStateToProps = ({ currentUser }) => ({
    authenticationToken: currentUser.authenticationToken,
    tenantId: currentUser.tenantId,
});

const mapDispatchToProps = {
    addNotification,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ServiceController);
